import { useState, useEffect, useCallback, useRef } from 'react';
import conversationApi from '../services/conversationApi';
import socketService from '../services/socketService';

/**
 * Hook pour gérer les notifications de messages non lus
 * @param {boolean} pauseUpdates - Si true, pause les mises à jour automatiques
 */
export const useMessageNotifications = (pauseUpdates = false) => {
  const [unreadCounts, setUnreadCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const loadUnreadCountsRef = useRef();
  const pauseUpdatesRef = useRef(pauseUpdates);
  
  // Mettre à jour la ref quand pauseUpdates change
  useEffect(() => {
    pauseUpdatesRef.current = pauseUpdates;
  }, [pauseUpdates]);
  
  // Protection contre double appels API (React.StrictMode en développement)
  const apiCallsRef = useRef({
    loadUnreadCounts: false
  });

  // Charger les messages non lus
  const loadUnreadCounts = useCallback(async () => {
    // Protection contre double appels API (React.StrictMode en développement)
    if (apiCallsRef.current.loadUnreadCounts) {
      console.log('🚫 [loadUnreadCounts] Appel API bloqué (déjà en cours)');
      return;
    }

    apiCallsRef.current.loadUnreadCounts = true;

    try {
      setLoading(true);
      
      const response = await conversationApi.getUnreadCounts();
      if (response.success) {
        setUnreadCounts(response.data);
      } else {
        console.warn('📨 [useMessageNotifications] Échec du chargement:', response);
      }
    } catch (error) {
      console.error('📨 [useMessageNotifications] Erreur loading unread counts:', error);
    } finally {
      setLoading(false);
      apiCallsRef.current.loadUnreadCounts = false; // Réinitialiser le flag de protection
    }
  }, []);

  // Stocker la référence stable
  loadUnreadCountsRef.current = loadUnreadCounts;

  // Marquer une conversation comme lue
  const markConversationAsRead = useCallback((reservationId) => {
    setUnreadCounts(prev => {
      const newCounts = { ...prev };
      delete newCounts[reservationId];
      return newCounts;
    });
  }, []);

  // Charger les compteurs au montage (UNE SEULE FOIS)
  useEffect(() => {
    loadUnreadCounts();
  }, []); // PAS de dépendances pour éviter les boucles

  // Gérer les nouveaux messages via socket (SANS dépendance loadUnreadCounts)
  useEffect(() => {
    const handleNewMessage = (data) => {
      // Ne pas mettre à jour si les mises à jour sont en pause
      if (pauseUpdatesRef.current) {
        console.log('📨 [useMessageNotifications] Mise à jour pausée, message ignoré');
        return;
      }
      
      if (data.conversation_id && data.message) {
        // Recharger les compteurs via la ref stable
        loadUnreadCountsRef.current?.();
      }
    };

    const handleMessageRead = (data) => {
      // Ne pas mettre à jour si les mises à jour sont en pause
      if (pauseUpdatesRef.current) {
        console.log('📨 [useMessageNotifications] Mise à jour pausée, lecture ignorée');
        return;
      }
      
      // Recharger les compteurs via la ref stable
      loadUnreadCountsRef.current?.();
    };

    socketService.on('new_message', handleNewMessage);
    socketService.on('messages_read_all', handleMessageRead);

    return () => {
      socketService.off('new_message', handleNewMessage);
      socketService.off('messages_read_all', handleMessageRead);
    };
  }, []); // PAS de dépendances pour éviter les re-exécutions

  return {
    unreadCounts,
    loading,
    loadUnreadCounts,
    markConversationAsRead,
    hasUnreadMessages: (reservationId) => (unreadCounts[reservationId] || 0) > 0,
    getUnreadCount: (reservationId) => unreadCounts[reservationId] || 0,
    refreshCounts: () => {
      // Fonction pour rafraîchir manuellement les compteurs (ex: après fermeture de modal)
      if (!pauseUpdatesRef.current) {
        loadUnreadCountsRef.current?.();
      }
    }
  };
};

export default useMessageNotifications;
